<template>
  <div class="social-reporting-filters">
    <div class="social-reporting-filters__body">
      <v-row dense align="end">
        <v-col
          v-if="showYear"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Année</label>
          <rmr-select
            v-model="draft.year"
            :items="years"
            label="Sélectionner une année"
          />
        </v-col>
        <v-col
          v-if="showDates"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Période</label>
          <rmr-date-picker-field
            class="social-reporting-filter-field__date"
            :value="dateRange"
            :display-value="dateRangeLabel"
            label="Sélectionner une période"
            :prepend-inner-icon="icons.mdiCalendarRange"
            :picker-props="{ max: todayDate }"
            range
            close-on-input
            @input="updateDateRange"
          />
        </v-col>
        <v-col
          v-if="postFilters"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Publications</label>
          <rmr-select
            v-model="draft.basis"
            :items="basisOptions"
            item-text="text"
            item-value="value"
            label="Créées ou vues sur la période"
          />
        </v-col>
        <v-col
          v-if="postFilters"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field social-reporting-filter-field--search"
        >
          <label class="rmr-table-shell__filter-label">Recherche</label>
          <rmr-text-field
            v-model.trim="draft.search"
            label="Titre, message ou description"
            append-icon="mdi-magnify"
            clearable
            @keyup.enter="apply"
          />
        </v-col>
        <v-col
          v-if="showBrand"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Marques</label>
          <rmr-autocomplete
            v-model="draft.brandIds"
            :items="options.brands"
            item-text="displayName"
            item-value="id"
            label="Toutes les marques"
            multiple
            no-chips
            clearable
          />
        </v-col>
        <v-col
          v-if="showNetwork"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Réseaux</label>
          <rmr-autocomplete
            v-model="draft.networks"
            :items="networkOptions"
            item-text="text"
            item-value="value"
            label="Tous les réseaux"
            multiple
            no-chips
            clearable
          />
        </v-col>
        <v-col
          v-if="showAccount"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Comptes</label>
          <rmr-autocomplete
            v-model="draft.accountIds"
            :items="accountOptions"
            item-text="displayName"
            item-value="id"
            label="Tous les comptes"
            multiple
            no-chips
            clearable
          />
        </v-col>
        <v-col
          v-if="showSite"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">{{ siteLabel }}</label>
          <rmr-autocomplete
            v-model="draft.siteIds"
            :items="options.sites"
            item-text="displayName"
            item-value="id"
            :label="`Tous les ${siteLabel.toLowerCase()}`"
            multiple
            no-chips
            clearable
          />
        </v-col>
        <v-col
          v-if="showSource"
          cols="12"
          sm="6"
          md="3"
          class="social-reporting-filter-field"
        >
          <label class="rmr-table-shell__filter-label">Sources</label>
          <rmr-select
            v-model="draft.sources"
            :items="options.sources"
            label="Toutes les sources"
            multiple
            no-chips
            clearable
          />
        </v-col>

        <template v-if="postFilters">
          <v-col cols="12" sm="6" md="3" class="social-reporting-filter-field">
            <label class="rmr-table-shell__filter-label">Auteurs</label>
            <rmr-autocomplete
              v-model="draft.authors"
              :items="options.authors"
              label="Tous les auteurs"
              multiple
              no-chips
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" class="social-reporting-filter-field">
            <label class="rmr-table-shell__filter-label"
              >Types de publication</label
            >
            <rmr-select
              v-model="draft.postTypes"
              :items="options.postTypes"
              label="Tous les types"
              multiple
              no-chips
              clearable
            />
          </v-col>
        </template>

        <v-col cols="12" md="auto" class="social-reporting-filters__actions">
          <rmr-action-button
            label="Réinitialiser"
            :icon="icons.mdiRefresh"
            variant="transparent"
            :color="RMR_DANGER"
            size="sm"
            :disabled="loading"
            @click="reset"
          />
          <rmr-action-button
            label="Appliquer"
            :icon="icons.mdiFilterOutline"
            variant="primary"
            size="sm"
            :loading="loading"
            @click="apply"
          />
          <slot name="actions" />
        </v-col>
      </v-row>

      <v-alert v-if="dateError" dense text type="error" class="mt-4 mb-0">
        {{ dateError }}
      </v-alert>
    </div>
  </div>
</template>

<script>
import { mdiCalendarRange, mdiFilterOutline, mdiRefresh } from "@mdi/js";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrDatePickerField from "@/components/ui/forms/RmrDatePickerField.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import { RMR_DANGER } from "@/styles/tokens";
import { NETWORK_LABELS, POST_BASIS_OPTIONS } from "./config";

const emptyFilters = () => ({
  dateFrom: "",
  dateTo: "",
  basis: "published",
  brandIds: [],
  networks: [],
  accountIds: [],
  siteIds: [],
  sources: [],
  authors: [],
  postTypes: [],
  search: "",
});

const cloneFilters = (filters) => ({
  ...emptyFilters(),
  ...filters,
  brandIds: [...(filters.brandIds || [])],
  networks: [...(filters.networks || [])],
  accountIds: [...(filters.accountIds || [])],
  siteIds: [...(filters.siteIds || [])],
  sources: [...(filters.sources || [])],
  authors: [...(filters.authors || [])],
  postTypes: [...(filters.postTypes || [])],
});

export default {
  name: "SocialReportingFilters",
  components: {
    RmrActionButton,
    RmrAutocomplete,
    RmrDatePickerField,
    RmrSelect,
    RmrTextField,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showDates: {
      type: Boolean,
      default: true,
    },
    postFilters: {
      type: Boolean,
      default: false,
    },
    datesRequired: {
      type: Boolean,
      default: false,
    },
    showYear: {
      type: Boolean,
      default: false,
    },
    years: {
      type: Array,
      default: () => [],
    },
    showNetwork: {
      type: Boolean,
      default: true,
    },
    showBrand: {
      type: Boolean,
      default: true,
    },
    showAccount: {
      type: Boolean,
      default: true,
    },
    showSite: {
      type: Boolean,
      default: true,
    },
    showSource: {
      type: Boolean,
      default: true,
    },
    siteLabel: {
      type: String,
      default: "Sites",
    },
  },
  data() {
    return {
      basisOptions: POST_BASIS_OPTIONS,
      draft: cloneFilters(this.value),
      dateError: "",
      icons: { mdiCalendarRange, mdiFilterOutline, mdiRefresh },
      RMR_DANGER,
    };
  },
  computed: {
    dateRange() {
      return [this.draft.dateFrom, this.draft.dateTo].filter(Boolean);
    },
    dateRangeLabel() {
      return this.dateRange.join(" → ");
    },
    todayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    networkOptions() {
      return (this.options.networks || []).map((network) => ({
        text: NETWORK_LABELS[network] || network,
        value: network,
      }));
    },
    accountOptions() {
      const selectedNetworks = this.draft.networks || [];
      return (this.options.accounts || [])
        .filter(
          (account) =>
            !selectedNetworks.length ||
            selectedNetworks.includes(account.network)
        )
        .map((account) => ({
          ...account,
          displayName: `${account.name || account.external_account_id} · ${
            NETWORK_LABELS[account.network] || account.network
          }`,
        }));
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.draft = cloneFilters(value);
      },
    },
  },
  methods: {
    updateDateRange(value) {
      if (!Array.isArray(value)) return;
      const normalizedDates = value.filter(Boolean).sort();
      this.draft = {
        ...this.draft,
        dateFrom: normalizedDates[0] || "",
        dateTo: normalizedDates[1] || "",
      };
      if (normalizedDates.length === 2) this.dateError = "";
    },
    apply() {
      this.dateError = "";
      if (this.showYear && !this.draft.year) {
        this.dateError = "Sélectionnez une année.";
      } else if (
        this.showDates &&
        this.datesRequired &&
        (!this.draft.dateFrom || !this.draft.dateTo)
      ) {
        this.dateError =
          "Une date de début et une date de fin sont nécessaires pour charger les publications.";
      } else if (
        this.showDates &&
        this.draft.dateFrom &&
        this.draft.dateTo &&
        this.draft.dateFrom > this.draft.dateTo
      ) {
        this.dateError =
          "La date de début doit être antérieure ou égale à la date de fin.";
      } else if (
        this.showDates &&
        this.draft.dateFrom &&
        this.draft.dateTo &&
        (Date.parse(`${this.draft.dateTo}T00:00:00Z`) -
          Date.parse(`${this.draft.dateFrom}T00:00:00Z`)) /
          86400000 >
          30
      ) {
        this.dateError = "La période ne peut pas dépasser 31 jours.";
      }
      if (this.dateError) return;
      this.$emit("input", cloneFilters(this.draft));
      this.$emit("apply");
    },
    reset() {
      this.dateError = "";
      this.draft = emptyFilters();
      this.$emit("input", cloneFilters(this.draft));
      this.$emit("reset");
    },
  },
};

export { emptyFilters };
</script>

<style lang="scss" scoped>
.social-reporting-filters {
  background: $rmr-surface-0;
}

.social-reporting-filters__actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: $rmr-space-xs;
  margin-left: auto;
  padding-bottom: 4px;
}

.social-reporting-filters__body {
  padding: $rmr-space-xs $rmr-space-lg $rmr-space-md;
}

.social-reporting-filter-field {
  min-width: 0;
}

.social-reporting-filter-field__date {
  width: 100%;
}

@media (max-width: 600px) {
  .social-reporting-filters__actions {
    justify-content: flex-end;
    margin-left: 0;
    width: 100%;
  }
}
</style>
